import { formatDateTimeBR, formatNumberCurrency } from "../Components/Utils";

/**
 * Janela de validade do câmbio — texto pronto para a tela.
 *
 * Contexto (2026-09-03/04): as telas diziam "câmbio de hoje". Era falso. A taxa da SMI é publicada
 * uma vez por dia e a janela **atravessa a virada do dia** — quem abria às 9h via um número de
 * ontem, expirando no meio da manhã, com a tela prometendo que valia "hoje".
 *
 * O backend respondeu em 2026-09-04 (`be-resposta-janela-do-cambio-2026-09-04.md`) mandando três
 * campos novos: `atualizadoEm`, `validoAte` (agora em TODOS os status) e `congelado`. E confirmou o
 * essencial: **a validade sai do que a SMI declara** (`validUntil`), ao segundo — não de hora
 * nominal, dos dois lados.
 *
 * Por isso este módulo NÃO calcula janela nenhuma. Ele só formata o que veio. Derivar o horário de
 * publicação aqui recriaria a regra em dois lugares, que é a forma exata do BUG-004.
 *
 * O `expirado` existe por uma ressalva que o próprio BE levantou: se a API da SMI falhar num dia, a
 * taxa antiga continua em uso e o `validoAte` dela fica no passado. Nesse caso não podemos escrever
 * "válido até" no futuro — seria promessa vencida com cara de promessa viva.
 */

/** Formata só a parte de dinheiro, com o símbolo que as telas já usam. */
const brl = (n) => formatNumberCurrency(Number(n), "BRL");

/**
 * @param {Object} cambio  `{usdBrl, atualizadoEm, validoAte, congelado, data}` do payload
 * @param {Date}   agora   injetável para teste
 * @returns {null|{usdBrl, publicadoEm, valeAte, congelado, expirado, texto, textoCurto}}
 */
export const descreverCambio = (cambio, agora = new Date()) => {
	if (!cambio || cambio.usdBrl == null) return null;

	const usdBrl = Number(cambio.usdBrl);
	if (!Number.isFinite(usdBrl)) return null;

	// `atualizadoEm` é o campo novo; `data` é o legado, que o BE mantém com o mesmo valor por
	// compatibilidade. Quando ele for aposentado, esta linha perde o fallback e nada mais muda.
	const publicadoEm = formatDateTimeBR(cambio.atualizadoEm || cambio.data || "");
	const valeAte = formatDateTimeBR(cambio.validoAte || "");
	const congelado = cambio.congelado === true;

	const fim = cambio.validoAte ? new Date(cambio.validoAte) : null;
	const expirado = !congelado && !!fim && !isNaN(fim.getTime()) && fim.getTime() < agora.getTime();

	let texto;
	let textoCurto;

	if (congelado) {
		// Cotação encerrada: não existe validade futura, e inventar uma data seria mentir.
		texto = publicadoEm
			? `Câmbio congelado de ${brl(usdBrl)} por dólar, em ${publicadoEm}.`
			: `Câmbio congelado de ${brl(usdBrl)} por dólar.`;
		textoCurto = `câmbio congelado · ${brl(usdBrl)}`;
	} else if (expirado) {
		texto = `Câmbio de ${brl(usdBrl)} por dólar, publicado em ${publicadoEm}. A validade informada pelo fornecedor venceu em ${valeAte} — o valor pode ser atualizado a qualquer momento.`;
		textoCurto = `câmbio ${brl(usdBrl)} · validade vencida`;
	} else if (valeAte) {
		texto = publicadoEm
			? `Câmbio de ${brl(usdBrl)} por dólar, publicado em ${publicadoEm}, válido até ${valeAte}.`
			: `Câmbio de ${brl(usdBrl)} por dólar, válido até ${valeAte}.`;
		textoCurto = `câmbio ${brl(usdBrl)} · vale até ${valeAte}`;
	} else {
		// Sem `validoAte` (payload antigo). Não anunciamos validade nenhuma — melhor informar de
		// menos do que prometer um prazo que não veio.
		texto = publicadoEm
			? `Câmbio de ${brl(usdBrl)} por dólar, publicado em ${publicadoEm}.`
			: `Câmbio de ${brl(usdBrl)} por dólar.`;
		textoCurto = `câmbio ${brl(usdBrl)}`;
	}

	return { usdBrl, publicadoEm, valeAte, congelado, expirado, texto, textoCurto };
};
